import { cn, ModeToggle, toast } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Delete } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { eventsApi } from "@/modules/events/presentation/api/client";
import { authClient } from "../api/client";
import { PIN_KEYPAD, PIN_MAX_LENGTH } from "../lib/pin-login-shared";
import {
  persistJudgeCompetitionEventId,
  useCompetitionAuth,
} from "../model/useCompetitionAuth";

const JUDGE_STYLES: Record<
  number,
  {
    label: string;
    subject: string;
    email: string;
    activeClass: string;
    dotClass: string;
  }
> = {
  1: {
    label: "Judge 1",
    subject: "Dance Skill & Technique",
    email: "judge1@system.local",
    activeClass: "bg-accent text-accent-foreground",
    dotClass: "bg-accent",
  },
  2: {
    label: "Judge 2",
    subject: "Stage Presence & Performance",
    email: "judge2@system.local",
    activeClass: "bg-chart-3 text-primary-foreground",
    dotClass: "bg-chart-3",
  },
  3: {
    label: "Judge 3",
    subject: "Concept & Creativity",
    email: "judge3@system.local",
    activeClass: "bg-chart-5 text-primary-foreground",
    dotClass: "bg-chart-5",
  },
};

export function JudgePinLoginPage() {
  const { eventId, judgeNumber: judgeNumberParam } = useParams({
    from: "/competition/judge-login/$eventId/$judgeNumber",
  });
  const judgeNumber = Number.parseInt(judgeNumberParam, 10);
  const slot = useMemo(() => {
    if (judgeNumber !== 1 && judgeNumber !== 2 && judgeNumber !== 3)
      return null;
    return JUDGE_STYLES[judgeNumber];
  }, [judgeNumber]);

  const [pin, setPin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const {
    isLoading: authLoading,
    isCompetitionUser,
    getRedirectPath,
  } = useCompetitionAuth();

  const eventQuery = useQuery({
    queryKey: ["events", "public-detail", eventId],
    queryFn: () => eventsApi.getEvent(eventId),
    enabled: Boolean(eventId) && slot != null,
    staleTime: 60_000,
  });
  const eventRow = eventQuery.data;

  const displayName =
    slot == null
      ? null
      : judgeNumber === 1
        ? eventRow?.judgeDisplayNames?.judge1
        : judgeNumber === 2
          ? eventRow?.judgeDisplayNames?.judge2
          : eventRow?.judgeDisplayNames?.judge3;

  useEffect(() => {
    if (authLoading || !isCompetitionUser || !slot) return;
    navigate({ to: getRedirectPath() });
  }, [authLoading, isCompetitionUser, getRedirectPath, navigate, slot]);

  const handleKey = (key: string) => {
    if (key === "⌫") {
      setPin((p) => p.slice(0, -1));
    } else if (pin.length < PIN_MAX_LENGTH) {
      setPin((p) => p + key);
    }
  };

  const handleSubmit = async () => {
    if (pin.length === 0 || !slot || Number.isNaN(judgeNumber)) return;
    setIsLoading(true);
    try {
      await authClient.signIn.email(
        {
          email: slot.email,
          password: pin,
          fetchOptions: {
            headers: {
              "X-Competition-Event-Id": eventId,
            },
          },
        },
        {
          onSuccess: () => {
            persistJudgeCompetitionEventId(eventId);
            toast.success(`ເຂົ້າລະບົບສໍາເລັດ — ${slot.label}`);
            window.location.assign(
              `/competition/events/${eventId}/judge/${judgeNumber}`,
            );
          },
          onError: (err) => {
            toast.error("PIN ບໍ່ຖືກຕ້ອງ ຫຼື ບໍ່ສາມາດເຂົ້າລະບົບໄດ້");
            setPin("");
            console.error(err);
          },
        },
      );
    } catch (e) {
      toast.error("PIN ບໍ່ຖືກຕ້ອງ ຫຼື ບໍ່ສາມາດເຂົ້າລະບົບໄດ້");
      setPin("");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: auto-submit on full PIN
  useEffect(() => {
    if (slot && pin.length === PIN_MAX_LENGTH) {
      void handleSubmit();
    }
  }, [pin, slot]);

  if (!slot) {
    return (
      <div className="flex h-svh flex-col items-center justify-center gap-4 bg-background px-4">
        <p className="text-muted-foreground text-sm">ລິ້ງບໍ່ຖືກຕ້ອງ</p>
        <button
          type="button"
          className="text-primary text-sm underline"
          onClick={() => void navigate({ to: "/competition/login" })}
        >
          ໄປໜ້າເຂົ້າຜູ້ຄຸ້ມຄອງ
        </button>
      </div>
    );
  }

  if (authLoading || eventQuery.isPending) {
    return (
      <div className="flex h-svh items-center justify-center bg-background">
        <div className="size-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (eventQuery.isError) {
    return (
      <div className="flex h-svh flex-col items-center justify-center gap-4 bg-background px-4">
        <p className="text-muted-foreground text-sm">ບໍ່ພົບງານ ຫຼື ລິ້ງບໍ່ຖືກຕ້ອງ</p>
        <button
          type="button"
          className="text-primary text-sm underline"
          onClick={() => void navigate({ to: "/competition/login" })}
        >
          ໄປໜ້າຜູ້ຄຸ້ມຄອງ
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="fixed top-4 right-4 z-50">
        <ModeToggle />
      </div>
      <div className="kpop-bg flex min-h-svh flex-col items-center justify-center bg-background px-4 py-8">
        <div className="mb-8 text-center">
          <p className="font-bold text-accent text-xs uppercase tracking-widest">
            KiDS EXPO 2026
          </p>
          <h1 className="kpop-gradient-text mt-1 font-extrabold text-5xl leading-tight">
            K-POP
          </h1>
          <h2 className="font-extrabold text-2xl text-foreground leading-tight">
            COVER DANCE
          </h2>
          <p className="mt-1 font-semibold text-chart-5 text-sm uppercase tracking-widest">
            Open Division
          </p>
        </div>

        <div className="w-full max-w-sm rounded-2xl bg-card p-6 shadow-2xl ring-1 ring-border">
          {/* <button
            type="button"
            onClick={() => void navigate({ to: "/competition/login" })}
            className="mb-4 flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            ຜູ້ຄຸ້ມຄອງ
          </button> */}

          <p className="mb-1 text-center font-semibold text-foreground text-sm">
            {slot.label}
          </p>
          <p className="mb-1 text-center text-muted-foreground text-xs">
            {slot.subject}
          </p>
          <p className="mb-1 text-center font-medium text-foreground text-sm">
            {eventRow?.name ?? "…"}
          </p>
          {displayName ? (
            <p className="mb-2 text-center font-medium text-foreground text-sm">
              {displayName}
            </p>
          ) : null}

          <p className="mb-2 text-center text-muted-foreground text-xs leading-relaxed">
            ໃສ່ PIN ສຳລັບຕຳແໜ່ງນີ້ຂອງງານນີ້
          </p>
          <p className="mb-3 text-center text-muted-foreground text-sm">
            ໃສ່ PIN
          </p>

          <div className="mb-6 flex justify-center gap-3">
            {Array.from({ length: PIN_MAX_LENGTH }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed length
                key={i}
                className={cn(
                  "size-4 rounded-full transition-all duration-150",
                  i < pin.length
                    ? `${slot.dotClass} scale-110 shadow-md`
                    : "bg-muted",
                )}
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {PIN_KEYPAD.map(({ id, value }) => {
              if (value === "") return <div key={id} aria-hidden />;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleKey(value)}
                  disabled={isLoading}
                  className={cn(
                    "flex h-14 items-center justify-center rounded-xl font-bold text-xl transition-all active:scale-95 disabled:opacity-50",
                    value === "⌫"
                      ? "bg-muted text-muted-foreground hover:bg-muted/80"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                  )}
                >
                  {value === "⌫" ? <Delete className="size-5" /> : value}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isLoading || pin.length === 0}
            className={cn(
              "mt-4 w-full rounded-xl py-3 font-bold text-base transition-all active:scale-[0.98] disabled:opacity-40",
              slot.activeClass,
              "shadow-lg",
            )}
          >
            {isLoading ? "ກໍາລັງເຂົ້າລະບົບ..." : `ເຂົ້າລະບົບ — ${slot.label}`}
          </button>
        </div>

        <p className="mt-6 text-center text-muted-foreground/50 text-xs">
          ລະບົບໃຫ້ຄະແນນ · KiDS EXPO 2026
        </p>
      </div>
    </>
  );
}
